import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import CheckBox from 'components/CheckBox'
import Tooltip from 'components/Tooltip'
import NumberInput from 'components/Inputs/NumberInput'
import MainButton from 'components/Buttons/MainButton'
import AlertDialog from 'components/AlertDialog'
import payoutConfigValidator, { MAX_FEE_PERCENTAGE } from 'validators/payoutConfigValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Formik } from 'formik'
import { FaExclamationTriangle } from 'react-icons/fa'
import { DateTime } from 'luxon'

import {
  Content,
  Card,
  CardTitle,
  Form,
  Column,
  CheckRow,
  Label,
  FieldLabel,
  DataList,
  DataRow,
  DataLabel,
  DataValue,
  NoticeCard,
  InfoText,
  WarningText
} from '../styles'

/*
  A chave geral do repasse automático e a taxa de antecipação — as duas linhas de `settings`
  que decidem se dinheiro sai da conta da Cutilei e quanto é retido de quem antecipa.

  Existem três travas em série, e esta tela cuida de uma. As outras duas ficam fora daqui de
  propósito: `PAYOUT_START_DATE` mora no ambiente (ninguém deve poder reabrir o passado inteiro
  por uma tela de configuração), e a adesão de cada salão é consequência de a conta Cutilei Pay
  dele ter sido ativada.
*/
export default function PayoutConfig () {
  const [loading, setLoading] = useState (true)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const [error, setError] = useState (null)
  const [config, setConfig] = useState ({
    enabled: false,
    anticipationFeePercentage: 5,
    maxFeePercentage: MAX_FEE_PERCENTAGE,
    dueDays: 30,
    startDateConfigured: false,
    startDate: null
  })

  const requestConfig = AuthService.getRequestAuth ()

  useEffect (() => {
    getData ()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getData = async () => {
    try {
      const { data } = await cutileiApi.get ('/admin/payout_config', requestConfig)

      setConfig ({
        enabled: data.enabled ?? false,
        anticipationFeePercentage: data.anticipationFeePercentage ?? 5,
        maxFeePercentage: data.maxFeePercentage ?? MAX_FEE_PERCENTAGE,
        dueDays: data.dueDays ?? 30,
        startDateConfigured: data.startDateConfigured ?? false,
        startDate: data.startDate ?? null
      })
      setError (null)
    } catch (error) {
      logApiError (error)
      setError (error.response?.data?.message ?? 'Não foi possível carregar a configuração')
    } finally {
      setLoading (false)
    }
  }

  const toggleOkDialog = () => setShowOkDialog (!showOkDialog)

  if (loading) return <Loading style={{marginTop: 30}}/>

  const startDate = config.startDate
    ? DateTime.fromISO (config.startDate).setLocale ('pt-BR')
    : null

  return (
    <Formik
      initialValues={{
        enabled: config.enabled,
        anticipationFeePercentage: config.anticipationFeePercentage
      }}
      enableReinitialize
      validationSchema={payoutConfigValidator}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        try {
          setSubmitting (true)
          setError (null)

          const { data } = await cutileiApi.post (
            '/admin/payout_config', values, requestConfig
          )

          setConfig (prev => ({
            ...prev,
            enabled: data.enabled,
            anticipationFeePercentage: data.anticipationFeePercentage,
            startDateConfigured: data.startDateConfigured,
            startDate: data.startDate
          }))

          resetForm ({ values })
          setSubmitting (false)
          toggleOkDialog ()
        } catch (error) {
          setSubmitting (false)
          logApiError (error)

          /*
            Aqui a mensagem da API vai para a tela, ao contrário do resto das telas de
            configuração, que só chamam `logApiError` e ficam caladas.

            São duas recusas com explicação pronta e acionável — `start_date_missing` e
            `invalid_fee` —, e sem mostrá-las o financeiro clicaria em "Salvar" vendo a chave
            voltar para desligada sem nenhuma pista do motivo.
          */
          setError (
            error.response?.data?.message ?? 'Não foi possível salvar a configuração'
          )
        }
      }}
    >
      {({
        values,
        errors,
        dirty,
        isSubmitting,
        setFieldValue,
        handleSubmit
      }) => (
        <Content>
          {/*
            O aviso fica de pé enquanto a variável não existir, inclusive com a chave geral
            ligada: sem o marco zero a varredura não gera recebível nenhum, e o sintoma de
            esquecê-la é silêncio, não erro.
          */}
          {!config.startDateConfigured && (
            <NoticeCard>
              <FaExclamationTriangle size={16} style={{marginTop: 2, minWidth: 16}}/>
              <p>
                <b>PAYOUT_START_DATE não está configurada no ambiente.</b>
                {' Sem ela nenhum recebível é gerado, mesmo com o repasse ligado. Ela precisa '
                + 'receber a data do último fechamento manual — nunca a data do deploy, ou a '
                + 'varredura enxerga atendimentos já pagos por Pix e os paga de novo. '
                + 'Transferência interna do Asaas é instantânea e irreversível.'}
              </p>
            </NoticeCard>
          )}
          <Card>
            <Form>
              <CardTitle>Repasse automático</CardTitle>
              <CheckRow>
                <CheckBox
                  id='payout_enabled'
                  value={values.enabled}
                  onValueChange={value => setFieldValue ('enabled', value)}
                />
                <Label htmlFor='payout_enabled'>
                  Gerar e pagar os repasses automaticamente
                </Label>
                <Tooltip
                  id='payout_enabled'
                  labelStyle={{width: 320}}
                  message={
                    'A chave geral do repasse. Desligada, os recebíveis param de ser gerados '
                    + 'e nenhuma transferência sai — é o botão de pânico do financeiro, lido '
                    + 'a cada execução das tarefas, sem cache. Os recebíveis já criados '
                    + 'continuam existindo e voltam a ser pagos quando ela for religada.'
                  }
                />
              </CheckRow>
              <InfoText>
                {'Cada atendimento de membro validado pelo salão gera dois recebíveis — o '
                + 'líquido do salão e a comissão da manicure — pagos '
                + `${config.dueDays} dias depois da data do atendimento.`}
              </InfoText>

              <Column gap={5}>
                <FieldLabel htmlFor='anticipationFeePercentage'>
                  Taxa de antecipação (%)
                </FieldLabel>
                <NumberInput
                  id='anticipationFeePercentage'
                  name='anticipationFeePercentage'
                  value={values.anticipationFeePercentage}
                  minValue={0}
                  maxValue={config.maxFeePercentage}
                  step={1}
                  onChange={setFieldValue}
                  style={{width: 140}}
                />
                {errors.anticipationFeePercentage && (
                  <WarningText>{errors.anticipationFeePercentage}</WarningText>
                )}
                <InfoText>
                  {'Percentual retido quando o salão ou a manicure escolhe receber antes do '
                  + 'vencimento. '
                  + 'A taxa é congelada no pedido, então alterá-la aqui não reprecifica '
                  + 'antecipações já feitas.'}
                </InfoText>
              </Column>

              {error && <WarningText>{error}</WarningText>}

              {dirty && (
                <MainButton
                  label='Salvar alterações'
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  width={160}
                  style={{marginTop: 5}}
                />
              )}
            </Form>
          </Card>

          {/*
            Leitura, e não configuração: as duas são decisões de implantação, e quem opera
            precisa poder conferi-las sem abrir a task definition do ECS.
          */}
          <Card gap={10}>
            <CardTitle>Implantação</CardTitle>
            <DataList>
              <DataRow>
                <DataLabel>Marco zero (PAYOUT_START_DATE)</DataLabel>
                <DataValue>
                  {startDate?.isValid
                    ? startDate.toFormat ('dd/MM/yyyy')
                    : 'Não configurada'}
                </DataValue>
              </DataRow>
              <DataRow>
                <DataLabel>Prazo de pagamento</DataLabel>
                <DataValue>{`${config.dueDays} dias após o atendimento`}</DataValue>
              </DataRow>
              <DataRow>
                <DataLabel>Teto da taxa de antecipação</DataLabel>
                <DataValue>{`${config.maxFeePercentage}%`}</DataValue>
              </DataRow>
            </DataList>
            <InfoText>
              {'Só são gerados recebíveis de atendimentos a partir do marco zero, e ele muda '
              + 'pelo ambiente, não por esta tela. A adesão de cada salão acontece quando a '
              + 'conta Cutilei Pay dele é ativada.'}
            </InfoText>
          </Card>

          <AlertDialog
            visible={showOkDialog}
            title='Sucesso'
            message='Configuração do repasse atualizada!'
            confirmText='Ok'
            onConfirm={toggleOkDialog}
            onClose={toggleOkDialog}
          />
        </Content>
      )}
    </Formik>
  )
}
